import axios, {
  AxiosError,
  AxiosInstance,
  InternalAxiosRequestConfig,
} from "axios";
import * as Sentry from "@sentry/react";
import Cookies from "js-cookie";
import { API_HOST } from "../constants/common.ts";
import {
  getDemoFolderIdFromPathname,
  getStoredVaultActorToken,
  isDemoModeActive,
} from "../utils/vault/vaultActorSession.ts";

// Errors that should not be captured to Sentry (expected/non-actionable)
const IGNORED_ERROR_MESSAGES = [
  "Network Error",
  "AbortError",
  "cancelled",
  "The operation was aborted",
  "timeout of 0ms exceeded",
];

export type ApiAuthMode =
  | "session-first"
  | "session-only"
  | "jwt-only"
  | "none";

export function resolveAuthorizationHeader(
  authMode: ApiAuthMode,
  sessionToken?: string,
  actorJwt?: string | null,
) {
  if (authMode === "none") return null;
  if (authMode === "jwt-only") {
    return actorJwt ? `Bearer ${actorJwt}` : null;
  }
  if (authMode === "session-only") {
    return sessionToken ? `token ${sessionToken}` : null;
  }
  if (sessionToken) return `token ${sessionToken}`;
  return actorJwt ? `Bearer ${actorJwt}` : null;
}

export function normalizeAuthorizationErrorData(
  data: unknown,
): Record<string, unknown> {
  const originalData =
    typeof data === "object" && data !== null && !Array.isArray(data)
      ? (data as Record<string, unknown>)
      : {};
  const backendCode =
    typeof originalData.code === "string" ? originalData.code : undefined;

  return {
    ...originalData,
    ...(backendCode ? { backendCode } : {}),
    code: "I000",
  };
}

function createApiClient(authMode: ApiAuthMode = "session-first") {
  const client = axios.create({
    baseURL: API_HOST,
  });

  attachInterceptors(client, authMode);
  return client;
}

function attachInterceptors(client: AxiosInstance, authMode: ApiAuthMode) {
  client.interceptors.request.use(
    (config: InternalAxiosRequestConfig) => {
      const authorizationHeader = resolveAuthorizationHeader(
        authMode,
        Cookies.get("session"),
        getStoredVaultActorToken(),
      );

      if (authorizationHeader) {
        config.headers["Authorization"] = authorizationHeader;
      } else {
        delete config.headers["Authorization"];
      }

      const demoFolderId = getDemoFolderIdFromPathname(
        window.location.pathname,
      );
      const isDocvaultRequest = config.url?.includes("/docvault/") ?? false;
      if (isDocvaultRequest && demoFolderId && isDemoModeActive(demoFolderId)) {
        config.headers["X-Demo-Mode"] = "true";
      } else {
        delete config.headers["X-Demo-Mode"];
      }

      // Add breadcrumb for API request tracking
      Sentry.addBreadcrumb({
        category: "api",
        message: `${config.method?.toUpperCase()} ${config.url}`,
        level: "info",
        data: {
          method: config.method?.toUpperCase(),
          url: config.url,
        },
      });

      return config;
    },
    (error: AxiosError) => Promise.reject(error),
  );

  client.interceptors.response.use(
    (response) => response,
    (error: AxiosError) => {
      const status = error.response?.status;
      const url = error.config?.url;
      const method = error.config?.method?.toUpperCase();
      const errorMessage = error.message || "";

      const isIgnoredError = IGNORED_ERROR_MESSAGES.some((msg) =>
        errorMessage.includes(msg),
      );

      if (axios.isCancel(error) || isIgnoredError) {
        return Promise.reject(error);
      }

      Sentry.addBreadcrumb({
        category: "api.error",
        message: `${status || "Network Error"} ${url}`,
        level: "error",
        data: {
          status,
          url,
          method,
          errorCode: (error.response?.data as { code?: string })?.code,
        },
      });

      if (status === 401 || status === 403) {
        Sentry.addBreadcrumb({
          category: "auth",
          message: `Authentication error: ${status}`,
          level: "warning",
          data: { status, url },
        });

        return Promise.reject({
          ...error,
          response: {
            ...error.response,
            status,
            data: normalizeAuthorizationErrorData(error.response?.data),
          },
        });
      }

      Sentry.withScope((scope) => {
        scope.setTag("api_error", "true");
        scope.setTag("http_status", status?.toString() || "network_error");

        scope.setContext("request", {
          url,
          method,
          baseURL: error.config?.baseURL,
        });

        scope.setContext("response", {
          status,
          statusText: error.response?.statusText,
          data:
            typeof error.response?.data === "object"
              ? JSON.stringify(error.response?.data).substring(0, 500)
              : error.response?.data,
        });

        if (status && status >= 500) {
          scope.setLevel("error");
        } else if (status && status >= 400) {
          scope.setLevel("warning");
        }

        scope.setFingerprint([
          "api-error",
          method || "unknown",
          url?.replace(/\/\d+/g, "/:id") || "unknown",
          status?.toString() || "network",
        ]);

        Sentry.captureException(error);
      });

      return Promise.reject(error);
    },
  );
}

const api = createApiClient("session-first");

export const vaultActorApi = createApiClient("jwt-only");
export const staffApi = createApiClient("session-only");
export const vaultPublicApi = createApiClient("none");

export default api;
